<template>
  <div
    :id="`port-${port.id}`"
    ref="element"
    class="port"
    @click.stop="onClick"
  >
    <slot
      :port="port"
      :port-disabled="disabled"
      :connection-count="connectionCount"
    >
      <div class="port__default" />
    </slot>
  </div>
</template>

<script setup lang="ts" generic="SCHEMA extends INodeSchemaRegistry = INodeSchemaRegistry">
import {
  onBeforeUnmount,
  onMounted, shallowRef, useTemplateRef,
} from 'vue';

import {
  GraphEditor,
} from '../core';
import {
  IGraphEditorEvents,
  INodeSchemaRegistry,
  IPort,
} from '../types';

import {
  ISlotPropsPort, IUserInterfaceAdapterProps,
} from './types';

const props = defineProps<IUserInterfaceAdapterProps & {
  graphEditor: GraphEditor<SCHEMA>,
  port: IPort,
}>();

defineSlots<{
  default(props: ISlotPropsPort): void,
}>();

const element = useTemplateRef('element');
const connectionCount = shallowRef<number>(0);

function updateConnectionCount(event: IGraphEditorEvents['connection:created'] | IGraphEditorEvents['connection:deleted']) {
  if (
    (event.fromNodeId === props.port.nodeId && event.fromPortKey === props.port.key)
    || (event.toNodeId === props.port.nodeId && event.toPortKey === props.port.key)
  ) {
    connectionCount.value = props.graphEditor.getPortConnectionCount(props.port.nodeId, props.port.key);
  }
}

onMounted(() => {
  props.graphEditor.setPortElement(props.port.id, element.value!);

  props.graphEditor.addEventListener('connection:created', updateConnectionCount);
  props.graphEditor.addEventListener('connection:deleted', updateConnectionCount);

  connectionCount.value = props.graphEditor.getPortConnectionCount(props.port.nodeId, props.port.key);
});

onBeforeUnmount(() => {
  props.graphEditor.removeEventListener('connection:created', updateConnectionCount);
  props.graphEditor.removeEventListener('connection:deleted', updateConnectionCount);
});

function onClick() {
  if (props.disabled) {
    return;
  }
  if (props.graphEditor.hasActiveConnectionDraft()) {
    props.graphEditor.completeConnectionDraft(props.port.nodeId, props.port.key);
  } else {
    props.graphEditor.startConnectionDraft(props.port.nodeId, props.port.key);
  }
}
</script>

<style scoped lang="scss">
.port {
  &__default {
    width: 1.5rem;
    height: 1.5rem;
    border: 1px solid #000;
    border-radius: 50%;
    background-color: #fff;

    &:hover {
      background-color: #c7c7c7
    }

    &:active {
      cursor: default;
    }
  }
}
</style>
